import { Box, Divider } from '@mui/material'
import React, { useRef } from 'react'

import { Item } from './Select'

interface MultiSelectListBoxProps {
  items: Item[]
  value: any[]
  onChange: (newValue: any[]) => void
  minWidth?: number | string
  maxWidth?: number | string
  disabled?: boolean
  size?: number
}

/**
 * Custom multi-select listbox used by `Select` whenever it is rendered in
 * `native && multiple` mode (e.g. inside the column-menu filter dropdowns).
 *
 * Behavior expected by users (and not provided by the native
 * `<select multiple>` on the web platform):
 *  - click toggles a single item (no Cmd/Ctrl required)
 *  - shift-click selects the range between the last clicked item ("anchor")
 *    and the current one, applying the anchor's selection state to the range
 *  - Cmd/Ctrl-click also toggles a single item (same as plain click)
 */
const MultiSelectListBox: React.FC<MultiSelectListBoxProps> = ({
  items,
  value = [],
  onChange,
  minWidth,
  maxWidth,
  disabled,
  size = 5
}) => {
  const anchorIndexRef = useRef<number | null>(null)

  const selectedSet = new Set(value)

  const handleClick = (event: React.MouseEvent, index: number) => {
    if (disabled) return
    const clickedItem = items[index]
    if (!clickedItem || clickedItem.isSeperator) return

    const next = new Set(selectedSet)

    if (event.shiftKey && anchorIndexRef.current !== null && anchorIndexRef.current !== index) {
      const anchorIndex = anchorIndexRef.current
      const start = Math.min(anchorIndex, index)
      const end = Math.max(anchorIndex, index)
      // The anchor's current state defines whether the range gets selected or deselected.
      const anchorItem = items[anchorIndex]
      const anchorSelected = anchorItem ? selectedSet.has(anchorItem.value) : true
      for (let i = start; i <= end; i += 1) {
        const it = items[i]
        if (!it || it.isSeperator) continue
        if (anchorSelected) next.add(it.value)
        else next.delete(it.value)
      }
    } else {
      if (selectedSet.has(clickedItem.value)) next.delete(clickedItem.value)
      else next.add(clickedItem.value)
      anchorIndexRef.current = index
    }

    onChange(Array.from(next))
  }

  // Approximate the native <select size="N"> visual height so the listbox
  // stays as compact as the original control.
  const rowHeightPx = 22
  const verticalPaddingPx = 8
  const maxHeight = `${size * rowHeightPx + verticalPaddingPx}px`

  return (
    <Box
      role="listbox"
      aria-multiselectable
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      sx={theme => ({
        minWidth,
        maxWidth,
        maxHeight,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        padding: '4px 0',
        outline: 'none',
        userSelect: 'none',
        backgroundColor: theme.palette.background.paper,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'default',
        '&:focus-visible': {
          borderColor: theme.palette.primary.main
        }
      })}
    >
      {items.map((item, index) => {
        if (!item) return null
        if (item.isSeperator) {
          // eslint-disable-next-line react/no-array-index-key
          return <Divider key={`sep-${index}`} sx={{ my: 0.5 }} />
        }
        const selected = selectedSet.has(item.value)
        return (
          <Box
            key={item.value ?? index}
            role="option"
            aria-selected={selected}
            // Prevent text selection / focus jumping when shift-clicking across rows.
            onMouseDown={e => e.preventDefault()}
            onClick={e => handleClick(e, index)}
            title={item.label}
            sx={{
              padding: '2px 10px',
              fontSize: '0.875rem',
              lineHeight: `${rowHeightPx - 4}px`,
              cursor: disabled ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: selected ? '#ffffff' : 'inherit',
              backgroundColor: selected ? '#9ca3af' : 'transparent',
              '&:hover': {
                backgroundColor: selected ? '#4b5563' : '#6b7280',
                color: '#ffffff'
              }
            }}
          >
            {item.label}
          </Box>
        )
      })}
    </Box>
  )
}

export default MultiSelectListBox
